import React from 'react'
import { Add, DeleteOutline } from '@mui/icons-material'
import { Divider, IconButton, Paper, Tooltip, Typography } from '@mui/material'
import { createStyles, withStyles } from '@mui/styles'
import { defaultColors, defaultStyles, muiOptions, MuiProps } from '../../infrastructure/materialUiThemeProvider'
import { t } from '../../infrastructure/i18nextHelper'
import { Button, CustomDialog, DatePicker, NumberField, Popup, Select, Switch, TextField } from '../common/customComponents'
import { RecipeEditContainer, recipeDialog } from './recipeEditStore'
import { ProductRecipeItem, RoadSalesProductRecipe } from './recipeModels'

function RecipeEdit({ classes }: MuiProps) {
	let store = RecipeEditContainer.useContainer()
	let title = t(store.isNew ? 'recipes.form.createTitle' : 'recipes.form.editTitle')
	let buttons = store.editorState ? [{ buttonText: t('recipes.actions.save'), buttonEffect: store.save }] : []

	return (
		<Popup title={title}
			close={recipeDialog.close}
			buttons={buttons}
			isOpen={store.isOpen}>
			<div>
				{store.editorState
					? <RecipeForm classes={classes} form={store.editorState} store={store} />
					: <Typography className={classes.empty}>{t('recipes.form.loading')}</Typography>}
				<CustomDialog
					isOpen={store.isReplaceDialogOpen}
					title={t('recipes.replace.title')}
					contentText={t('recipes.replace.text')}
					confirmButtonText={t('recipes.replace.yes')}
					onConfirm={store.confirmSave}
					onCancel={() => store.setIsReplaceDialogOpen(false)}
					onClose={() => store.setIsReplaceDialogOpen(false)} />
			</div>
		</Popup>
	)
}

function RecipeForm({ classes, form, store }: MuiProps & { form: RoadSalesProductRecipe, store: any }) {
	let update = (changes: Partial<RoadSalesProductRecipe>) => store.updateForm(current => ({ ...current, ...changes }))
	let roadSalesProductChoices = store.roadSalesProducts.map(x => ({
		value: x.id,
		text: store.oneCompanyInCountry
			? `${x.code} - ${x.description}`
			: `${x.companyCode} - ${x.code} - ${x.description}`
	}))
	let localProductChoices = store.localProducts.map(x => ({
		value: x.productId,
		text: `${x.product} - ${x.name}`
	}))
	let siteChoices = [
		{ value: '', text: t('recipes.form.genericSite') },
		...store.sites.map(x => ({ value: x.code, text: x.name }))
	]

	return (
		<div className={classes.formContent}>
			<Paper className={classes.formPaper}>
				<Typography className={classes.paperTitle} variant='overline' display='block' gutterBottom>{t('recipes.form.title')}</Typography>
				<div className={classes.descriptionRow}>
					<Select
						label={t('recipes.form.roadSalesProduct')}
						choices={roadSalesProductChoices}
						value={form.roadSalesProductId}
						disabled={!store.isNew}
						onChange={value => update({ roadSalesProductId: value as any })} />
					<TextField
						label={t('recipes.form.description')}
						text={form.description}
						maxLength={300}
						onChange={event => update({ description: event.target.value })} />
				</div>
				<div className={classes.formRow}>
					<Select
						label={t('recipes.form.site')}
						choices={siteChoices}
						value={form.site ?? ''}
						disabled={!store.isNew}
						onChange={value => update({ site: value || null })} />
					<DatePicker
						label={t('recipes.form.validFrom')}
						date={form.validFrom}
						disabled={!store.isNew}
						setDate={date => update({ validFrom: date })} />
					<Switch
						form
						label={t('recipes.form.active')}
						offText={t('recipes.form.no')}
						onText={t('recipes.form.yes')}
						isChecked={form.active}
						changeCallback={() => update({ active: !form.active })} />
				</div>
				<Divider className={classes.divider} />
				<Typography className={classes.componentsTitle} variant='h6'>{t('recipes.form.components')}</Typography>
				<div>
					{form.items.map((item, index) =>
						<div className={classes.itemRow} key={index}>
							<Select
								label={t('recipes.form.localProduct')}
								choices={localProductChoices}
								value={item.productId ?? ''}
								onChange={value => store.setComponent(index, { ...item, productId: value as ProductRecipeItem['productId'] })} />
							<NumberField
								label={t('recipes.form.percentage')}
								text={item.percentage === null ? null : item.percentage * 100}
								min={0}
								max={100}
								precision={4}
								onChange={percentage => store.setComponent(index, {
									...item,
									percentage: percentage === null ? null : percentage / 100
								})} />
							<Tooltip title={t('recipes.actions.removeComponent')}>
								<span>
									<IconButton disabled={form.items.length === 1} onClick={() => store.removeComponent(index)}>
										<DeleteOutline />
									</IconButton>
								</span>
							</Tooltip>
							{index === 0 && <Button img={<Add />} label={t('recipes.actions.addComponent')} onClick={store.addComponent} />}
						</div>
					)}
				</div>
			</Paper>
		</div>
	)
}

let styles = () => createStyles({
	formContent: {
		width: '100%',
		padding: '1em 0',
		boxSizing: 'border-box'
	},
	empty: {
		padding: '2em'
	},
	formPaper: {
		padding: '1em',
		width: '100%',
		minHeight: 'calc(100vh - 4.6em)',
		boxSizing: 'border-box'
	},
	paperTitle: {
		color: defaultColors.red.main.color
	},
	descriptionRow: {
		...defaultStyles.flexRow,
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: '0.5em',
		marginBottom: '0.75em',
		'& > div:last-child': {
			width: '30.6em',
			maxWidth: 'calc(100% - 1em)'
		}
	},
	formRow: {
		...defaultStyles.flexRow,
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: '0.5em',
		marginBottom: '0.75em'
	},
	componentsTitle: {
		marginBottom: '0.5em'
	},
	divider: {
		margin: '1em 0'
	},
	itemRow: {
		...defaultStyles.flexRow,
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: '0.5em',
		padding: '0.5em 0',
		'&:not(:last-child)': {
			borderBottom: '1px solid #ddd'
		}
	}
})

export default withStyles(styles, muiOptions)(RecipeEdit)

